import React, { useState, useEffect } from 'react';
import { Card, Button, ShopSettingsTab, SupportHelpTab } from '@/components';
import { BaileysQRConnect } from '@/components/BaileysQRConnect';
import { Toast } from '@/components/Toast';
import { useAuth } from '@/hooks';
import { getToken, buildApiUrl } from '@/utils';
import { Modal } from '@/components/ui/Modal';

interface ConnectionStatus {
  status: 'connected' | 'disconnected';
  phone?: string;
  connectedAt?: string;
}

/**
 * Local helper component for Subscription Plan cards layout
 */
const SubscriptionTab: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="text-center max-w-xl mx-auto space-y-2 mb-6">
        <h2 className="text-2xl font-bold text-slate-900">Choose the Right Plan for Your Shop</h2>
        <p className="text-sm text-slate-600">
          Scale your print automation. Simple, transparent pricing with no hidden charges.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Monthly Plan */}
        <Card className="relative p-6 border border-slate-200 bg-white flex flex-col justify-between hover:shadow-lg hover:border-slate-300 transition-all duration-300 group rounded-2xl">
          <div>
            <div className="space-y-1">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Starter</span>
              <h3 className="text-lg font-bold text-slate-900">Monthly Plan</h3>
            </div>
            <div className="my-6">
              <span className="text-3xl font-extrabold text-slate-900">₹199</span>
              <span className="text-slate-500 text-xs font-semibold"> / month</span>
            </div>
            <ul className="space-y-3 text-xs text-slate-600 border-t border-slate-100 pt-4">
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> Connect 1 WhatsApp Number
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> Auto-download PDF & Images
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> 1-Click Print Queue
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> Standard Order Dashboard
              </li>
              <li className="flex items-center gap-2 text-slate-400">
                <span>✕</span> Priority Chat Support
              </li>
            </ul>
          </div>
          <Button variant="secondary" size="md" className="w-full mt-8 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
            Subscribe Monthly
          </Button>
        </Card>

        {/* Half-Yearly Plan */}
        <Card className="relative p-6 border-2 border-blue-500 bg-white flex flex-col justify-between hover:shadow-xl transition-all duration-300 group rounded-2xl shadow-[0_8px_30px_rgba(59,130,246,0.08)]">
          <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md">
            Most Popular
          </div>
          <div>
            <div className="space-y-1">
              <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Grow</span>
              <h3 className="text-lg font-bold text-slate-900">Half-Yearly Plan</h3>
            </div>
            <div className="my-6">
              <span className="text-3xl font-extrabold text-slate-900">₹999</span>
              <span className="text-slate-500 text-xs font-semibold"> / 6 months</span>
              <div className="text-[10px] text-green-600 font-bold mt-1">Save ~16% compared to monthly</div>
            </div>
            <ul className="space-y-3 text-xs text-slate-600 border-t border-slate-100 pt-4">
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> Connect 1 WhatsApp Number
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> Auto-download PDF & Images
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> 1-Click Print Queue
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> Standard Order Dashboard
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> Priority Chat Support
              </li>
            </ul>
          </div>
          <Button variant="primary" size="md" className="w-full mt-8 shadow-md">
            Subscribe Half-Yearly
          </Button>
        </Card>

        {/* Yearly Plan */}
        <Card className="relative p-6 border border-slate-200 bg-white flex flex-col justify-between hover:shadow-lg hover:border-slate-300 transition-all duration-300 group rounded-2xl">
          <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md">
            Best Value
          </div>
          <div>
            <div className="space-y-1">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">Enterprise</span>
              <h3 className="text-lg font-bold text-slate-900">Yearly Plan</h3>
            </div>
            <div className="my-6">
              <span className="text-3xl font-extrabold text-slate-900">₹1999</span>
              <span className="text-slate-500 text-xs font-semibold"> / year</span>
              <div className="text-[10px] text-green-600 font-bold mt-1">Save ~16% compared to monthly</div>
            </div>
            <ul className="space-y-3 text-xs text-slate-600 border-t border-slate-100 pt-4">
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> Connect 1 WhatsApp Number
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> Auto-download PDF & Images
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> 1-Click Print Queue
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> Enhanced Analytics & Dashboard
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500 font-bold">✓</span> 24/7 Dedicated Support
              </li>
            </ul>
          </div>
          <Button variant="secondary" size="md" className="w-full mt-8 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
            Subscribe Yearly
          </Button>
        </Card>
      </div>
    </div>
  );
};

/**
 * Baileys WhatsApp Settings Page
 * Desktop-first page for connecting WhatsApp via Baileys QR code and managing Shop details
 */
export const BaileysWhatsAppSettingsPage: React.FC = () => {
  const { admin } = useAuth();
  const shopId = admin?.shop_id || '';
  const isOwner = admin?.role === 'owner';

  const [activeTab, setActiveTab] = useState<'whatsapp' | 'shop' | 'support' | 'subscription'>('whatsapp');

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    status: 'disconnected',
  });

  const [toast, setToast] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Fetch connection status on mount
  useEffect(() => {
    if (shopId) {
      fetchConnectionStatus();
    } else {
      setIsLoading(false);
    }
  }, [shopId]);

  const fetchConnectionStatus = async () => {
    try {
      const token = getToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      const response = await fetch(buildApiUrl(`shops/${shopId}/status`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setConnectionStatus({
          status: data.status === 'connected' ? 'connected' : 'disconnected',
          phone: data.phone,
        });
      } else if (response.status === 401 || response.status === 403) {
        console.warn('Auth error fetching WhatsApp status:', response.status);
      } else {
        console.error('Failed to fetch WhatsApp status:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectSuccess = (phone?: string) => {
    setConnectionStatus({ status: 'connected', phone });
    setToast({
      type: 'success',
      message: 'WhatsApp connected successfully! Orders will start appearing in your dashboard.',
    });
  };

  const handleConnectError = (error: string) => {
    setToast({
      type: 'error',
      message: `Connection failed: ${error}`,
    });
  };

  const handleDisconnect = async () => {
    if (!isOwner) return;
    
    setIsDisconnecting(true);

    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(buildApiUrl(`shops/${shopId}/disconnect`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setConnectionStatus({ status: 'disconnected' });
        setToast({
          type: 'success',
          message: 'WhatsApp disconnected successfully. You can now scan a new QR code.',
        });
        setIsDisconnectModalOpen(false);
      } else {
        const data = await response.json();
        setToast({
          type: 'error',
          message: `Failed to disconnect: ${data.error || 'Unknown error'}`,
        });
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      setToast({
        type: 'error',
        message: 'Failed to disconnect WhatsApp. Please try again.',
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (!shopId) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-8">
        <div className="mx-auto max-w-2xl">
          <Card className="border-l-4 border-l-yellow-500 bg-yellow-50 p-6">
            <p className="text-sm text-yellow-900">
              ⚠️ No shop ID found. Please ensure you're logged in and have selected a shop.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-4xl space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-950">Settings</h1>
          <p className="mt-2 text-slate-600">
            Configure your shop details, WhatsApp capture, and staff members
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors -mb-[2px] flex items-center gap-2 group ${
              activeTab === 'whatsapp'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <svg 
              className={`w-4 h-4 transition-colors ${
                activeTab === 'whatsapp' ? 'text-[#25D366]' : 'text-slate-400 group-hover:text-[#25D366]'
              }`} 
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.487-1.761-1.66-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            WhatsApp Link
          </button>
          <button
            onClick={() => setActiveTab('shop')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors -mb-[2px] ${
              activeTab === 'shop'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            🏢 Shop Management
          </button>
          <button
            onClick={() => setActiveTab('support')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors -mb-[2px] ${
              activeTab === 'support'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            ❓ Support / Help
          </button>
          <button
            onClick={() => setActiveTab('subscription')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors -mb-[2px] ${
              activeTab === 'subscription'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            💳 Subscription Plan
          </button>
        </div>

        {isLoading ? (
          <Card className="p-8 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600"></div>
            <p className="mt-4 text-slate-600">Loading connection status...</p>
          </Card>
        ) : activeTab === 'whatsapp' ? (
          <div className="grid gap-8 lg:grid-cols-3">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {connectionStatus.status === 'disconnected' ? (
                <BaileysQRConnect
                  shopId={shopId}
                  onConnected={handleConnectSuccess}
                  onError={handleConnectError}
                  disabled={!isOwner}
                />
              ) : (
                <>
                  {/* Connected Status */}
                  <Card className="border-l-4 border-l-green-500 bg-green-50 p-6">
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">✓</div>
                      <div>
                        <h3 className="text-lg font-semibold text-green-900">WhatsApp is Connected</h3>
                        <p className="mt-1 text-sm text-green-700">
                          Your WhatsApp account is actively connected and ready to receive orders.
                        </p>
                        {connectionStatus.phone && (
                          <div className="mt-3 rounded bg-white px-3 py-2">
                            <p className="text-xs text-slate-500">Connected Phone</p>
                            <p className="font-mono font-semibold text-slate-900">
                              {connectionStatus.phone}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>

                  {/* How It Works */}
                  <Card className="p-6">
                    <h3 className="font-semibold text-slate-950">How It Works</h3>
                    <ol className="mt-4 space-y-3 text-sm text-slate-600">
                      <li className="flex gap-3">
                        <span className="font-semibold text-blue-600">1.</span>
                        <span>Customers send documents to your WhatsApp number</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="font-semibold text-blue-600">2.</span>
                        <span>Documents are automatically downloaded and uploaded to our servers</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="font-semibold text-blue-600">3.</span>
                        <span>New orders appear in your dashboard instantly</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="font-semibold text-blue-600">4.</span>
                        <span>Print orders with one click, and manage everything from the dashboard</span>
                      </li>
                    </ol>
                  </Card>

                  {/* Supported File Types */}
                  <Card className="p-6">
                    <h3 className="font-semibold text-slate-950">Supported File Types</h3>
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      {[
                        'PDF (.pdf)',
                        'Word (.doc, .docx)',
                        'Excel (.xls, .xlsx)',
                        'PowerPoint (.ppt, .pptx)',
                        'Images (.jpg, .png, .heic)',
                        'Text & CSV (.txt, .csv)',
                      ].map((type) => (
                        <div key={type} className="flex items-center gap-2">
                          <span className="text-green-600">✓</span>
                          <span className="text-sm text-slate-600">{type}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Status Card */}
              <Card className="p-4">
                <p className="text-xs font-semibold uppercase text-slate-500">Status</p>
                <div className="mt-2 flex items-center gap-2">
                  <div
                    className={`h-2 w-2 rounded-full ${
                      connectionStatus.status === 'connected' ? 'bg-green-600' : 'bg-slate-400'
                    }`}
                  ></div>
                  <p className="text-sm font-medium text-slate-900 capitalize">
                    {connectionStatus.status}
                  </p>
                </div>
              </Card>

              {/* Quick Actions */}
              {connectionStatus.status === 'connected' && (
                <Card className="p-4">
                  <p className="text-xs font-semibold uppercase text-slate-500 mb-3">Actions</p>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    disabled={!isOwner}
                    onClick={() => setIsDisconnectModalOpen(true)}
                  >
                    Disconnect & Reset
                  </Button>
                  {!isOwner && (
                    <p className="mt-2 text-xs font-semibold text-slate-500 text-center">
                      🔒 Accessed by owner
                    </p>
                  )}
                </Card>
              )}

              {/* Info Box */}
              <Card className="bg-blue-50 p-4">
                <p className="text-xs font-semibold text-blue-900 mb-2">💡 Pro Tip</p>
                <p className="text-xs text-blue-800">
                  Your sessions are securely stored on our servers. You can disconnect and reconnect anytime.
                </p>
              </Card>

              {/* Help */}
              <Card className="p-4">
                <details>
                  <summary className="cursor-pointer text-xs font-semibold uppercase text-slate-500">
                    Need Help?
                  </summary>
                  <ul className="mt-3 space-y-2 text-xs text-slate-600">
                    <li>• Connection issues? Refresh and try again</li>
                    <li>• QR code not scanning? Check your phone camera</li>
                    <li>• Orders not appearing? Verify your WhatsApp number is correct</li>
                  </ul>
                </details>
              </Card>
            </div>
          </div>
        ) : activeTab === 'shop' ? (
          <ShopSettingsTab shopId={shopId} isOwner={isOwner} />
        ) : activeTab === 'support' ? (
          <SupportHelpTab />
        ) : (
          <SubscriptionTab />
        )}

        {/* Toast Notification */}
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}

        {/* Disconnect Confirmation Modal */}
        <Modal
          isOpen={isDisconnectModalOpen}
          title="Disconnect WhatsApp?"
          onClose={() => !isDisconnecting && setIsDisconnectModalOpen(false)}
          size="sm"
          actions={[
            {
              label: 'Disconnect & Reset',
              onClick: handleDisconnect,
              variant: 'danger',
              isLoading: isDisconnecting,
            },
          ]}
        >
          <p className="text-slate-600">
            Are you sure you want to disconnect WhatsApp? This will stop all new incoming orders until you scan a new QR code.
          </p>
        </Modal>
      </div>
    </div>
  );
};

export default BaileysWhatsAppSettingsPage;
